import { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, Star, Save, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, onRatingChange, label }) => {
  return (
    <div>
      <Label className="text-sm text-muted-foreground">{label}</Label>
      <div className="flex gap-1 mt-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRatingChange(star)}
            className={`p-1 transition-colors ${
              star <= rating ? 'text-yellow-400' : 'text-muted-foreground/30'
            }`}
          >
            <Star className="h-6 w-6 fill-current" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default function DebriefForm({ eventId, eventName }) {
  const [debrief, setDebrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    what_went_well: '',
    what_needs_improvement: '',
    would_return: true,
    return_reasoning: '',
    lessons_learned: '',
    booth_location_notes: '',
    crowd_traffic_rating: 3,
    vendor_support_rating: 3,
  });

  useEffect(() => {
    fetchDebrief();
  }, [eventId]);

  const fetchDebrief = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/events/${eventId}/debrief`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setDebrief(response.data);
        setFormData({
          what_went_well: response.data.what_went_well || '',
          what_needs_improvement: response.data.what_needs_improvement || '',
          would_return: response.data.would_return ?? true,
          return_reasoning: response.data.return_reasoning || '',
          lessons_learned: response.data.lessons_learned || '',
          booth_location_notes: response.data.booth_location_notes || '',
          crowd_traffic_rating: response.data.crowd_traffic_rating || 3,
          vendor_support_rating: response.data.vendor_support_rating || 3,
        });
      }
    } catch (error) {
      // No existing debrief is fine
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('vyrus_token');
      const params = new URLSearchParams();
      
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          params.append(key, value);
        }
      });

      await axios.post(
        `${BACKEND_URL}/api/events/${eventId}/debrief?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Debrief saved successfully');
      fetchDebrief();
    } catch (error) {
      toast.error('Failed to save debrief');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-muted-foreground text-center py-8">Loading debrief...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Post-Convention Debrief</h3>
        </div>
        {debrief && (
          <span className="flex items-center gap-1 text-sm text-green-400">
            <Check className="h-4 w-4" />
            Completed
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* What Went Well */}
        <Card className="bg-green-500/5 border-green-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-green-400">What Went Well</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.what_went_well}
              onChange={(e) => setFormData({ ...formData, what_went_well: e.target.value })}
              placeholder="Sales, booth traffic, team performance, etc."
              rows={4}
              className="bg-secondary/30 border-transparent focus:border-green-500"
            />
          </CardContent>
        </Card>

        {/* What Needs Improvement */}
        <Card className="bg-red-500/5 border-red-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-red-400">What Needs Improvement</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.what_needs_improvement}
              onChange={(e) => setFormData({ ...formData, what_needs_improvement: e.target.value })}
              placeholder="Challenges, issues, things to change next time..."
              rows={4}
              className="bg-secondary/30 border-transparent focus:border-red-500"
            />
          </CardContent>
        </Card>
      </div>

      {/* Would Return */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-4">
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-muted-foreground">Would you return to this convention?</Label>
              <div className="flex gap-4 mt-2">
                <Button
                  type="button"
                  variant={formData.would_return ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, would_return: true })}
                  className={formData.would_return ? 'bg-green-500 hover:bg-green-600' : ''}
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={!formData.would_return ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, would_return: false })}
                  className={!formData.would_return ? 'bg-red-500 hover:bg-red-600' : ''}
                >
                  No
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Reasoning</Label>
              <Textarea
                value={formData.return_reasoning}
                onChange={(e) => setFormData({ ...formData, return_reasoning: e.target.value })}
                placeholder="Why or why not..."
                rows={2}
                className="mt-1 bg-secondary/30 border-transparent focus:border-primary"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ratings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <StarRating
              rating={formData.crowd_traffic_rating}
              onRatingChange={(r) => setFormData({ ...formData, crowd_traffic_rating: r })}
              label="Crowd/Traffic Rating"
            />
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-4">
            <StarRating
              rating={formData.vendor_support_rating}
              onRatingChange={(r) => setFormData({ ...formData, vendor_support_rating: r })}
              label="Vendor Support Rating"
            />
          </CardContent>
        </Card>
      </div>

      {/* Additional Notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-sm text-muted-foreground">Booth Location Notes</Label>
          <Textarea
            value={formData.booth_location_notes}
            onChange={(e) => setFormData({ ...formData, booth_location_notes: e.target.value })}
            placeholder="Was the location good? Any issues with placement?"
            rows={3}
            className="mt-1 bg-secondary/30 border-transparent focus:border-primary"
          />
        </div>
        <div>
          <Label className="text-sm text-muted-foreground">Lessons Learned</Label>
          <Textarea
            value={formData.lessons_learned}
            onChange={(e) => setFormData({ ...formData, lessons_learned: e.target.value })}
            placeholder="Key takeaways for future events..."
            rows={3}
            className="mt-1 bg-secondary/30 border-transparent focus:border-primary"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Debrief'}
        </Button>
      </div>
    </div>
  );
}
